import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router";

import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

const STEPS = ["Connect repo", "Inspect", "Adopt"];

type AdoptionState =
  | "idle"
  | "inspecting"
  | "inspect_success"
  | "inspect_failed"
  | "reviewing"
  | "adopting"
  | "adopt_success"
  | "adopt_failed";

interface AdoptionErrorDetail {
  category: string;
  message: string;
  guidance: string;
  retryable: boolean;
  details?: { errors?: string[] };
  timestamp?: string;
  errorLog?: string;
}

interface AdoptResultData {
  alias: string;
  envVarsStored: number;
}

interface ContractCheck {
  id: string;
  passed: boolean;
  severity: "error" | "warning" | "info";
  message: string;
  autoFixable: boolean;
  fixSuggestion?: string;
}

interface DetectedService {
  name: string;
  type: "server" | "static" | "worker" | "cron";
  port?: number;
  dockerfilePath?: string;
  healthEndpoint?: string;
  internetFacing: boolean;
  requiredEnvVars?: string[];
  optionalEnvVars?: string[];
}

interface RestructuringGuideItem {
  title: string;
  reason: string;
  steps: string[];
  codeSnippet?: string;
  severity: "error" | "warning";
}

interface RepoAnalysis {
  projectType: "single_app" | "monorepo";
  language: string;
  framework: string;
  services: DetectedService[];
  contractChecks: ContractCheck[];
  rushaYml?: string;
  restructuringGuide?: RestructuringGuideItem[];
  repoFullName: string;
  defaultBranch: string;
}

/** Avatar background color by service type */
const SERVICE_AVATAR_COLORS: Record<string, string> = {
  server: "primary.main",
  static: "secondary.main",
  worker: "#7c3aed",
  cron: "warning.main",
};

/** Progress banner step labels (adopting state) */
const ADOPTION_STEPS = [
  "Inspecting repository",
  "Pushing rusha.yml to repo",
  "Installing push webhook",
  "Creating application record",
] as const;

export default function AdoptStackPage() {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const nav = useAppNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [adoptionState, setAdoptionState] = useState<AdoptionState>("idle");
  const [repoUrl, setRepoUrl] = useState("");
  const [branch, setBranch] = useState("");
  const [analysis, setAnalysis] = useState<RepoAnalysis | null>(null);
  const [inspectError, setInspectError] = useState<string | null>(null);
  const [stackName, setStackName] = useState("");
  const [pipelineMode, setPipelineMode] = useState("rusha_managed");
  const [adoptErrorDetail, setAdoptErrorDetail] =
    useState<AdoptionErrorDetail | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [adoptResult, setAdoptResult] = useState<AdoptResultData | null>(null);
  // envVarValues: envVarName → user-entered value
  const [envVarValues, setEnvVarValues] = useState<Record<string, string>>({});
  // manualEnvVars: user-added { name, value } pairs
  const [manualEnvVars, setManualEnvVars] = useState<
    { name: string; value: string }[]
  >([]);

  const inspecting = adoptionState === "inspecting";
  const adopting = adoptionState === "adopting";

  const errorChecks =
    analysis?.contractChecks.filter(
      (c) => !c.passed && c.severity === "error",
    ) ?? [];
  const canAdopt = errorChecks.length === 0;

  const allRequiredEnvVars =
    analysis?.services.flatMap((s) => s.requiredEnvVars ?? []) ?? [];
  const allOptionalEnvVars =
    analysis?.services.flatMap((s) => s.optionalEnvVars ?? []) ?? [];

  const anyUnfilledRequired = allRequiredEnvVars.some(
    (name) => !(envVarValues[name] ?? "").trim(),
  );

  function setEnvVarValue(name: string, value: string) {
    setEnvVarValues((prev) => ({ ...prev, [name]: value }));
  }

  function addManualEnvVar() {
    setManualEnvVars((prev) => [...prev, { name: "", value: "" }]);
  }

  function updateManualEnvVar(
    index: number,
    patch: Partial<{ name: string; value: string }>,
  ) {
    setManualEnvVars((prev) =>
      prev.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    );
  }

  function removeManualEnvVar(index: number) {
    setManualEnvVars((prev) => prev.filter((_, i) => i !== index));
  }

  function handleContinueToReview() {
    setActiveStep(2);
    setAdoptionState("reviewing");
  }

  function handleFixAndRetry() {
    setAdoptErrorDetail(null);
    setShowErrorDetails(false);
    setAdoptionState("reviewing");
  }

  function handleStartOver() {
    setAdoptErrorDetail(null);
    setShowErrorDetails(false);
    setAnalysis(null);
    setAdoptResult(null);
    setEnvVarValues({});
    setManualEnvVars([]);
    setStackName("");
    setRepoUrl("");
    setBranch("");
    setActiveStep(0);
    setAdoptionState("idle");
  }

  async function handleInspect() {
    if (!repoUrl.trim()) return;
    setAdoptionState("inspecting");
    setInspectError(null);
    setAnalysis(null);

    try {
      const response = await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/inspect`,
        { repoUrl, orgAlias, branch: branch || undefined },
        {
          withCredentials: true,
        },
      );
      setAnalysis(response.data as RepoAnalysis);
      setStackName(response.data.repoFullName.split("/")[1] ?? "");
      setActiveStep(1);
      setAdoptionState("inspect_success");
    } catch (err) {
      const axiosData = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })
        : null;
      setInspectError(
        axiosData?.message ??
          (err instanceof Error ? err.message : "Failed to inspect repository"),
      );
      setAdoptionState("inspect_failed");
    }
  }

  async function handleAdopt() {
    if (!orgAlias || !projectAlias || !repoUrl.trim()) return;
    setAdoptionState("adopting");
    setAdoptErrorDetail(null);

    // Collect all env vars (detected + manual) with non-empty values
    const envVars: { name: string; value: string }[] = [];
    for (const name of allRequiredEnvVars) {
      const val = envVarValues[name]?.trim();
      if (val) envVars.push({ name, value: val });
    }
    for (const name of allOptionalEnvVars) {
      const val = envVarValues[name]?.trim();
      if (val) envVars.push({ name, value: val });
    }
    for (const m of manualEnvVars) {
      if (m.name.trim() && m.value.trim()) {
        envVars.push({ name: m.name.trim(), value: m.value.trim() });
      }
    }

    try {
      const response = await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/adopt`,
        {
          repoUrl,
          projectAlias,
          name: stackName || undefined,
          pipelineMode,
          envVars: envVars.length > 0 ? envVars : undefined,
        },
        {
          withCredentials: true,
        },
      );
      const result = response.data as AdoptResultData;
      setAdoptResult(result);
      setAdoptionState("adopt_success");
      // Auto-navigate after 1.5s
      window.setTimeout(() => {
        nav.goToStackServices(orgAlias, projectAlias, result.alias);
      }, 1500);
    } catch (err) {
      const axiosData = axios.isAxiosError(err)
        ? (err.response?.data as Partial<AdoptionErrorDetail>)
        : null;
      setAdoptErrorDetail({
        category: axiosData?.category ?? "UNKNOWN",
        message:
          axiosData?.message ??
          (err instanceof Error ? err.message : "Failed to adopt repository"),
        guidance:
          axiosData?.guidance ??
          "Please try again. If the problem persists, contact support.",
        retryable: axiosData?.retryable ?? true,
        details: axiosData?.details,
        timestamp: new Date().toISOString(),
        errorLog: axios.isAxiosError(err)
          ? JSON.stringify(
              { status: err.response?.status, data: err.response?.data },
              null,
              2,
            )
          : err instanceof Error
            ? (err.stack ?? err.message)
            : String(err),
      });
      setAdoptionState("adopt_failed");
    }
  }

  function handleCancel() {
    nav.goToProjectDetails(orgAlias!, projectAlias!);
  }

  function handleBack() {
    setActiveStep((s) => Math.max(0, s - 1));
  }

  // ─── Meta row helper ───────────────────────────────────────────
  function MetaRow({ label, value }: { label: string; value: string }) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    );
  }

  // ─── Timeline item helper ──────────────────────────────────────
  function TimelineItem({
    color,
    label,
    time,
  }: {
    color: string;
    label: string;
    time: string;
  }) {
    return (
      <Stack
        direction="row"
        spacing={2}
        sx={{ py: 1.5, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color,
            mt: 0.75,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {time}
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Adopt a stack
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Connect a Git repository and let Rusha inspect, review, and adopt it as
        a managed stack.
      </Typography>

      {!isMobile && (
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label, index) => (
            <Step
              key={label}
              completed={index < activeStep}
              sx={{
                cursor: index < activeStep ? "pointer" : "default",
              }}
              onClick={() => {
                if (index < activeStep) setActiveStep(index);
              }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Error banner */}
      {inspectError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setInspectError(null);
                void handleInspect();
              }}
            >
              Try again
            </Button>
          }
        >
          {inspectError}
        </Alert>
      )}

      {/* ─── Step 0: Connect repo ─────────────────────────────────── */}
      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, maxWidth: 640 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Connect repository
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Repository URL
              </Typography>
              <TextField
                fullWidth
                placeholder="https://github.com/rusha-corp/llm-gateway"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleInspect();
                }}
                sx={{ fontFamily: "monospace" }}
                helperText="Public or private Git repositories are supported."
              />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Branch (optional)
              </Typography>
              <TextField
                fullWidth
                placeholder="main"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleInspect();
                }}
              />
            </Box>
            <Box>
              <Button
                variant="contained"
                onClick={handleInspect}
                disabled={!repoUrl.trim() || inspecting}
                sx={{ minWidth: 180 }}
              >
                {inspecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Connect & inspect"
                )}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ─── Step 1: Inspect ──────────────────────────────────────── */}
      {activeStep === 1 && inspecting && (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
          </Grid>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={80} />
        </Stack>
      )}

      {activeStep === 1 && analysis && !inspecting && (
        <Stack spacing={3}>
          {/* Row A: Repository info + Inspection status side-by-side (2:1 ratio) */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  Repository info
                </Typography>
                <MetaRow label="Repository" value={analysis.repoFullName} />
                <MetaRow label="Branch" value={analysis.defaultBranch} />
                <MetaRow label="Language" value={analysis.language} />
                <MetaRow label="Framework" value={analysis.framework || "—"} />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  Inspection status
                </Typography>
                <Stack spacing={1}>
                  {analysis.contractChecks.map((check) => (
                    <Box
                      key={check.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1,
                        px: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      {check.passed ? (
                        <CheckCircleIcon
                          color="success"
                          sx={{ fontSize: 20, flexShrink: 0 }}
                        />
                      ) : check.severity === "error" ? (
                        <ErrorIcon
                          color="error"
                          sx={{ fontSize: 20, flexShrink: 0 }}
                        />
                      ) : (
                        <WarningIcon
                          color="warning"
                          sx={{ fontSize: 20, flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">{check.message}</Typography>
                        {!check.passed && check.fixSuggestion && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              p: 1,
                              bgcolor: "action.hover",
                              borderRadius: 0.5,
                              overflow: "auto",
                              maxHeight: 120,
                              fontSize: "11px",
                              lineHeight: 1.5,
                              fontFamily: "monospace",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {check.fixSuggestion}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Row B: Detected services, full width */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Detected services
              </Typography>
              <Chip
                label={`${analysis.services.length} found`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Stack>
            <Stack spacing={1.5}>
              {analysis.services.map((svc) => (
                <Box
                  key={svc.name}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor:
                        SERVICE_AVATAR_COLORS[svc.type] ?? "primary.main",
                      fontSize: 16,
                      fontWeight: 700,
                      flexShrink: 0,
                      borderRadius: 1,
                    }}
                  >
                    {svc.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {svc.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type: {svc.type}
                      {svc.port ? ` · Port: ${svc.port}` : ""}
                      {svc.dockerfilePath
                        ? ` · Dockerfile: ${svc.dockerfilePath}`
                        : ""}
                      {svc.healthEndpoint
                        ? ` · Health: ${svc.healthEndpoint}`
                        : ""}
                    </Typography>
                  </Box>
                  {canAdopt && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleContinueToReview}
                      sx={{ flexShrink: 0 }}
                    >
                      Review
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Row C: Required Environment Variables callout */}
          {(allRequiredEnvVars.length > 0 || allOptionalEnvVars.length > 0) && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderColor: "rgba(245, 158, 11, 0.3)",
                bgcolor: "rgba(245, 158, 11, 0.06)",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: "warning.main", display: "flex", gap: 1 }}
                >
                  {allRequiredEnvVars.length > 0
                    ? "⚠ Required Environment Variables"
                    : "Environment Variables"}
                </Typography>
                <Chip
                  label={`${allRequiredEnvVars.length + allOptionalEnvVars.length} detected`}
                  size="small"
                  sx={{
                    bgcolor:
                      allRequiredEnvVars.length > 0
                        ? "rgba(239, 68, 68, 0.12)"
                        : "rgba(156, 163, 175, 0.12)",
                    color:
                      allRequiredEnvVars.length > 0
                        ? "error.main"
                        : "text.secondary",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Stack spacing={1.5}>
                {allRequiredEnvVars.map((name) => (
                  <Stack
                    key={name}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                  >
                    <Typography
                      component="code"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 13,
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: "action.hover",
                      }}
                    >
                      {name}
                    </Typography>
                    <Chip
                      label="Required"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 20, fontSize: 10 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      required by app config (detected in source code)
                    </Typography>
                  </Stack>
                ))}
                {allOptionalEnvVars
                  .filter((v) => !allRequiredEnvVars.includes(v))
                  .map((name) => (
                    <Stack
                      key={name}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      flexWrap="wrap"
                    >
                      <Typography
                        component="code"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 13,
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          bgcolor: "action.hover",
                        }}
                      >
                        {name}
                      </Typography>
                      <Chip
                        label="Optional"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        detected in source code
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                You&apos;ll configure these in the Review Services page before
                deploying.
              </Typography>
            </Paper>
          )}

          {/* Restructuring guide (if errors) */}
          {analysis.restructuringGuide &&
            analysis.restructuringGuide.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: "error.main",
                  bgcolor: "rgba(244,63,94,0.04)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="error.main"
                  gutterBottom
                >
                  {errorChecks.length} BLOCKER
                  {errorChecks.length > 1 ? "S" : ""} PREVENTING ADOPTION
                </Typography>
                {analysis.restructuringGuide.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mt: 2,
                      pl: 1,
                      borderLeft: 2,
                      borderColor:
                        item.severity === "error"
                          ? "error.main"
                          : "warning.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={
                        item.severity === "error"
                          ? "error.main"
                          : "warning.main"
                      }
                    >
                      {item.severity === "error" ? "⛔" : "⚠"} {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {item.reason}
                    </Typography>
                    {item.codeSnippet && (
                      <Box
                        component="pre"
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          fontSize: 13,
                          fontFamily: "monospace",
                          overflow: "auto",
                        }}
                      >
                        {item.codeSnippet}
                      </Box>
                    )}
                  </Box>
                ))}
              </Paper>
            )}

          {/* Row D: Page-level action row */}
          <Stack direction="row" justifyContent="space-between">
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleContinueToReview}>
              Continue to review →
            </Button>
          </Stack>
        </Stack>
      )}

      {/* ─── Step 2: Adopt (form persists across all adopt states) ── */}
      {activeStep === 2 &&
        analysis &&
        (adoptionState === "reviewing" ||
          adoptionState === "adopt_failed" ||
          adoptionState === "adopting" ||
          adoptionState === "adopt_success") && (
          <>
            {/* Inline progress banner (adopting) */}
            {adoptionState === "adopting" && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  borderColor: "primary.main",
                  bgcolor: "rgba(59, 130, 246, 0.04)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 2, pt: 2, pb: 1 }}
                >
                  <CircularProgress size={20} color="primary" />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    Adopting repository…
                  </Typography>
                </Stack>
                <Stack spacing={1} sx={{ px: 3, pb: 2 }}>
                  {ADOPTION_STEPS.map((label) => (
                    <Stack
                      key={label}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2">{label}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Inline error banner (adopt_failed) */}
            {adoptionState === "adopt_failed" && adoptErrorDetail && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  borderColor: "error.main",
                  bgcolor: "rgba(239, 68, 68, 0.04)",
                }}
              >
                {/* Header */}
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={1.5}
                  sx={{ p: 2, pb: showErrorDetails ? 0 : 2 }}
                >
                  <ErrorIcon color="error" sx={{ fontSize: 22, mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="error.main"
                    >
                      Adoption Failed
                    </Typography>
                    <Chip
                      label={adoptErrorDetail.category
                        .replace(/_/g, " ")
                        .toLowerCase()}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ mt: 0.5, textTransform: "capitalize" }}
                    />
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setShowErrorDetails((v) => !v)}
                    aria-expanded={showErrorDetails}
                  >
                    {showErrorDetails ? "Hide details ▴" : "Show details ▾"}
                  </Button>
                </Stack>

                {/* Body: message + guidance */}
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="body2">
                    {adoptErrorDetail.message}
                  </Typography>
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      How to fix:
                    </Typography>
                    {adoptErrorDetail.guidance}
                  </Alert>
                </Box>

                {/* Expandable details */}
                <Collapse in={showErrorDetails} unmountOnExit>
                  <Box
                    sx={{
                      mx: 2,
                      mb: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ width: 90 }}
                        >
                          Category:
                        </Typography>
                        <Typography variant="caption">
                          {adoptErrorDetail.category}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ width: 90 }}
                        >
                          Retryable:
                        </Typography>
                        <Typography variant="caption">
                          {adoptErrorDetail.retryable ? "Yes" : "No"}
                        </Typography>
                      </Stack>
                      {adoptErrorDetail.timestamp && (
                        <Stack direction="row" spacing={1}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ width: 90 }}
                          >
                            Timestamp:
                          </Typography>
                          <Typography variant="caption">
                            {new Date(
                              adoptErrorDetail.timestamp,
                            ).toLocaleString()}
                          </Typography>
                        </Stack>
                      )}
                      {adoptErrorDetail.details?.errors &&
                        adoptErrorDetail.details.errors.length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ mb: 0.5 }}
                            >
                              Issues found:
                            </Typography>
                            <Stack spacing={0.25}>
                              {adoptErrorDetail.details.errors.map(
                                (issue, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ pl: 2 }}
                                  >
                                    • {issue}
                                  </Typography>
                                ),
                              )}
                            </Stack>
                          </Box>
                        )}
                      {adoptErrorDetail.errorLog && (
                        <Box>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ mb: 0.5 }}
                          >
                            Error log:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              p: 1.5,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                              overflow: "auto",
                              maxHeight: 240,
                              fontSize: "12px",
                              lineHeight: 1.5,
                              fontFamily: "monospace",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {adoptErrorDetail.errorLog}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Collapse>

                {/* Actions */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ px: 2, py: 2, borderTop: 1, borderColor: "divider" }}
                >
                  {adoptErrorDetail.retryable && (
                    <Button variant="contained" onClick={handleFixAndRetry}>
                      Fix & retry
                    </Button>
                  )}
                  <Button variant="outlined" onClick={handleStartOver}>
                    Start over
                  </Button>
                </Stack>
              </Paper>
            )}

            {/* Inline success banner (adopt_success) */}
            {adoptionState === "adopt_success" && adoptResult && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  borderColor: "success.main",
                  bgcolor: "rgba(16, 185, 129, 0.04)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 2, pt: 2, pb: 1 }}
                >
                  <CheckCircleIcon color="success" sx={{ fontSize: 22 }} />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="success.main"
                  >
                    Stack adopted successfully!
                  </Typography>
                </Stack>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Stack alias
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontFamily="monospace"
                      >
                        {adoptResult.alias}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Env vars stored
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {adoptResult.envVarsStored}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <CircularProgress size={14} />
                    <Typography variant="body2" color="text.secondary">
                      Opening Review Services…
                    </Typography>
                    {orgAlias && projectAlias && (
                      <Button
                        size="small"
                        sx={{ ml: "auto" }}
                        onClick={() =>
                          nav.goToStackServices(
                            orgAlias,
                            projectAlias,
                            adoptResult.alias,
                          )
                        }
                      >
                        Open now →
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* Adopt form grid — dimmed while adopting/success */}
            <Grid
              container
              spacing={3}
              sx={{
                opacity:
                  adoptionState === "adopting" ||
                  adoptionState === "adopt_success"
                    ? 0.5
                    : 1,
                pointerEvents:
                  adoptionState === "adopting" ||
                  adoptionState === "adopt_success"
                    ? "none"
                    : "auto",
              }}
            >
              {/* Left column: What happens next */}
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                  >
                    What happens next
                  </Typography>
                  <TimelineItem
                    color={theme.palette.primary.main}
                    label="Rusha creates the stack record"
                    time="Immediately"
                  />
                  <TimelineItem
                    color={theme.palette.primary.main}
                    label="Initial build pipeline is queued"
                    time="~1 minute"
                  />
                  <TimelineItem
                    color={theme.palette.success.main}
                    label="Stack becomes available on the project page"
                    time="After first deploy"
                  />
                </Paper>
              </Grid>

              {/* Right column: Adopt summary + Stack configuration */}
              <Grid item xs={12} sm={8}>
                <Stack spacing={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 2 }}
                    >
                      Adopt summary
                    </Typography>
                    <MetaRow label="Repository" value={analysis.repoFullName} />
                    <MetaRow
                      label="Services"
                      value={`${analysis.services.length} detected`}
                    />
                    <MetaRow
                      label="Framework"
                      value={analysis.framework || "—"}
                    />
                    <MetaRow label="Default region" value="eu-central-1" />
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ mb: 2 }}
                    >
                      Stack configuration
                    </Typography>
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Stack alias
                        </Typography>
                        <TextField
                          fullWidth
                          value={stackName}
                          onChange={(e) => setStackName(e.target.value)}
                          helperText="Used in URLs, namespaces, and environment names."
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Deploy mode
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={pipelineMode}
                            onChange={(e) => setPipelineMode(e.target.value)}
                          >
                            <MenuItem value="rusha_managed">
                              Rusha managed
                            </MenuItem>
                            <MenuItem value="ci_build_rusha_deploy">
                              CI build, Rusha deploy
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Environment Variables inputs */}
                      {(allRequiredEnvVars.length > 0 ||
                        allOptionalEnvVars.length > 0 ||
                        manualEnvVars.length > 0) && (
                        <Box>
                          <Divider sx={{ mb: 2 }} />
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 1.5 }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              Environment Variables
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                              onClick={addManualEnvVar}
                              sx={{ textTransform: "none" }}
                            >
                              Add variable
                            </Button>
                          </Stack>
                          <Stack spacing={1}>
                            {allRequiredEnvVars.map((name) => {
                              const value = envVarValues[name] ?? "";
                              const isEmpty = !value.trim();
                              return (
                                <Stack
                                  key={name}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{
                                      width: 220,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      component="code"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: 13,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        bgcolor: "action.hover",
                                      }}
                                    >
                                      {name}
                                    </Typography>
                                    <Chip
                                      label="Required"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: 10 }}
                                    />
                                  </Box>
                                  <TextField
                                    size="small"
                                    type="password"
                                    placeholder="Enter value…"
                                    value={value}
                                    onChange={(e) =>
                                      setEnvVarValue(name, e.target.value)
                                    }
                                    sx={{ flex: 1, fontFamily: "monospace" }}
                                    error={isEmpty}
                                    InputProps={{
                                      startAdornment: isEmpty ? (
                                        <InputAdornment position="start">
                                          <WarningAmberIcon
                                            color="error"
                                            sx={{ fontSize: 16 }}
                                          />
                                        </InputAdornment>
                                      ) : undefined,
                                    }}
                                  />
                                </Stack>
                              );
                            })}
                            {allOptionalEnvVars
                              .filter((v) => !allRequiredEnvVars.includes(v))
                              .map((name) => (
                                <Stack
                                  key={name}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{
                                      width: 220,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      component="code"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: 13,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        bgcolor: "action.hover",
                                      }}
                                    >
                                      {name}
                                    </Typography>
                                    <Chip
                                      label="Optional"
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: 10 }}
                                    />
                                  </Box>
                                  <TextField
                                    size="small"
                                    type="password"
                                    placeholder="Optional…"
                                    value={envVarValues[name] ?? ""}
                                    onChange={(e) =>
                                      setEnvVarValue(name, e.target.value)
                                    }
                                    sx={{ flex: 1, fontFamily: "monospace" }}
                                  />
                                </Stack>
                              ))}
                            {manualEnvVars.map((m, idx) => (
                              <Stack
                                key={`manual-${idx}`}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <TextField
                                  size="small"
                                  placeholder="VARIABLE_NAME"
                                  value={m.name}
                                  onChange={(e) =>
                                    updateManualEnvVar(idx, {
                                      name: e.target.value,
                                    })
                                  }
                                  sx={{
                                    width: 220,
                                    flexShrink: 0,
                                    fontFamily: "monospace",
                                    "& .MuiOutlinedInput-root": {
                                      borderStyle: "dashed",
                                      borderWidth: 2,
                                    },
                                  }}
                                />
                                <TextField
                                  size="small"
                                  type="password"
                                  placeholder="Enter value…"
                                  value={m.value}
                                  onChange={(e) =>
                                    updateManualEnvVar(idx, {
                                      value: e.target.value,
                                    })
                                  }
                                  sx={{ flex: 1, fontFamily: "monospace" }}
                                />
                                <IconButton
                                  size="small"
                                  aria-label="Delete variable"
                                  onClick={() => removeManualEnvVar(idx)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                          </Stack>
                          {anyUnfilledRequired && (
                            <Alert
                              severity="warning"
                              sx={{
                                mt: 1.5,
                                py: 0.5,
                                "& .MuiAlert-message": { fontSize: 13 },
                              }}
                            >
                              Required variable(s) are empty. Fill them in
                              before adopting.
                            </Alert>
                          )}
                        </Box>
                      )}

                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          onClick={handleAdopt}
                          disabled={
                            adopting || !stackName.trim() || anyUnfilledRequired
                          }
                        >
                          {adopting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Confirm & adopt"
                          )}
                        </Button>
                        <Button variant="text" onClick={handleCancel}>
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
    </Container>
  );
}
